import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, MoreOutlined, SearchOutlined } from '@ant-design/icons';
import colors from '../../utils/colors';
import {
  ACCESS,
  CREATED_AT,
  DELETED,
  EMAIL,
  FIRST_NAME,
  ID,
  INVITE_USER,
  INVITED_AND_OLD_USERS,
  MEMBERSHIP_TYPE,
  SEARCH_BY_EMAIL
} from '../../utils/constants/labels';
import InviteUserForm from './InviteUserForm';
import CreationModal from '../common/modal/CreationModal';
import PageHeader from '../common/layout/PageHeader';
import Table from '../common/table/Table';
import { api } from '../../api/api';
import { DatePicker, Input, Row, Tag, Tooltip } from 'antd';
import { debounce } from '../../utils/common';
import { handleErrorResponse } from '../../api/network';
import { DatePreview, getSubdomain, membershipTypeColors, NotificationType, notify } from '../../utils/helpers';
import { useHistory } from 'react-router';
import SafeAction from '../common/SafeAction';
import { DATE_FORMAT, formatDate } from '../../utils/timeUtils';
import { MembershipType, User } from '../../types/userTypes';
import UserActionsModal from '../common/modal/UserActionsModal';
import { environment } from '../../config/configConstants';

const getColumns = (refreshTable, openUserActionsModal: (user: User) => void) => [
  {
    title: ID,
    dataIndex: 'id',
    sorter: true,
    width: '5%',
    defaultSortOrder: 'descend'
  },
  {
    title: FIRST_NAME,
    dataIndex: 'firstName',
    width: '10%'
  },
  {
    title: EMAIL,
    dataIndex: 'email',
    sorter: true,
    width: '15%'
  },
  {
    title: MEMBERSHIP_TYPE,
    dataIndex: 'membershipType',
    width: '10%',
    render: value => <Tag color={membershipTypeColors[value]}>{value}</Tag>
  },
  {
    title: CREATED_AT,
    dataIndex: 'createdAt',
    width: '10%',
    render: value => (value ? <DatePreview>{formatDate(value, DATE_FORMAT)}</DatePreview> : null)
  },
  {
    title: ACCESS,
    dataIndex: 'accessUntil',
    width: '35%',
    render: (value, row) => {
      if (value) {
        if (row.membershipType === MembershipType.INVITED) {
          return (
            <Row align={'middle'}>
              <DatePreview style={{ marginRight: '10px' }}>{formatDate(value, DATE_FORMAT)}</DatePreview>
              <SafeAction
                Icon={<DeleteOutlined />}
                action={() => api.grantLifetimeAccess(row.id)}
                onSuccess={() => {
                  notify('Lifetime access successfully granted', NotificationType.SUCCESS);
                  refreshTable();
                }}
                doneText={''}
                tooltip={'Convert to Lifetime'}
              />
            </Row>
          );
        }
        return <DatePreview>{formatDate(value, DATE_FORMAT)}</DatePreview>;
      } else {
        if (row.membershipType === MembershipType.INVITED) {
          return <GrantDemoForm id={row.id} refreshTable={refreshTable} />;
        }
        return null;
      }
    }
  },
  {
    title: DELETED,
    dataIndex: 'deleted',
    width: '5%',
    render: value => (value ? <CheckOutlined /> : <CloseOutlined />)
  },
  {
    title: '',
    dataIndex: 'resendInvitation',
    width: '5%',
    render: (value, row) => <SafeAction title={'Resend'} action={() => api.resendUserInvite(row.id)} doneText={'Sent'} />
  },
  {
    title: '',
    dataIndex: 'moreOptions',
    width: '5%',
    render: (value, row) =>
      !row.deleted && (
        <Tooltip title={'More options'}>
          <MoreOutlined onClick={() => openUserActionsModal(row)} />
        </Tooltip>
      )
  }
];

type Props = {};

const InvitedUsers: FC<Props> = ({}) => {
  const [email, setEmail] = useState('');
  const [editUser, setEditUser] = useState<User>();
  const [refreshTable, setRefreshTable] = useState(null);
  const history = useHistory();

  const setEmailDebounced = debounce(value => setEmail(value), 400);

  return (
    <Container>
      <PageHeader
        title={INVITED_AND_OLD_USERS}
        extra={[
          <Input
            key={1}
            onChange={e => setEmailDebounced(e.target.value)}
            placeholder={SEARCH_BY_EMAIL}
            prefix={<SearchOutlined />}
          />,
          <CreationModal
            key={2}
            title={INVITE_USER}
            onCreate={async values => {
              const response = await api.inviteUser(values);
              if (!response.ok) {
                handleErrorResponse(response, history);
                return false;
              }
              notify('User successfully invited', NotificationType.SUCCESS);
              // @ts-ignore
              await refreshTable();
              return true;
            }}
            ModalForm={InviteUserForm}
            bindActionButtons
          />
        ]}
      />
      <Table
        columns={getColumns(refreshTable, (user: User) => setEditUser(user))}
        getData={api.getAllInvitedUsers}
        idPropName={'id'}
        searchPropName={'email'}
        searchPropValue={email}
        bindFetchFn={setRefreshTable}
      />
      <UserActionsModal
        user={editUser ? { ...editUser, role: 'STUDENT' } : undefined}
        websiteSubdomain={getSubdomain(environment)}
        onClose={() => setEditUser(undefined)}
        onUserUpdate={refreshTable!}
      />
    </Container>
  );
};

export default InvitedUsers;

const GrantDemoForm: FC<{ id: number; refreshTable: Function }> = ({ id, refreshTable }) => {
  const [accessUntil, setAccessUntil] = useState<any>(null);

  return (
    <Row align={'middle'}>
      <DatePicker value={accessUntil} onChange={v => setAccessUntil(v)} style={{ marginRight: '10px' }} />
      <SafeAction
        Icon={<EditOutlined />}
        action={() => api.grantDemoAccess(id, accessUntil.toDate())}
        onSuccess={() => {
          notify('Demo access successfully granted', NotificationType.SUCCESS);
          refreshTable();
        }}
        doneText={''}
        disabled={!accessUntil}
        tooltip={'Convert to Demo'}
      />
    </Row>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;
